import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getClients } from "../../../lib/clients";
import { Avatar } from "../../ui";

export interface Recipient {
  address: string;
  /** The client's name, when the address was picked out of the book rather than
   *  typed. Kept only for the pill's label - only `address` is ever sent. */
  name?: string;
}

/** Deliberately loose. Strict RFC 5322 rejects addresses that real mail servers
 *  accept, and this check exists to catch a missing "@" or a stray comma, not to
 *  be the authority on what an address is - the server is. */
const LOOKS_LIKE_ADDRESS = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

export const isAddress = (s: string): boolean => LOOKS_LIKE_ADDRESS.test(s.trim());

export const asRecipients = (list: string[]): Recipient[] => list.map((address) => ({ address }));

interface Suggestion {
  id: string;
  name: string;
  address: string;
}

/**
 * Recipients as pills, with the client book behind them.
 *
 * The field it replaces was one text input holding comma-separated addresses.
 * That shape has two failures that only show up after Send: a typo is invisible
 * because every address looks alike in a run-on line, and there was no way to
 * remove the second of five without re-reading the whole string. A pill is a
 * thing you can see is wrong and a thing you can delete.
 *
 * The suggestions are real clients, searched live, not a fixed list: the address
 * worth writing to is almost always one this CRM already holds, and typing it
 * out by hand is how it ends up spelled two ways.
 */
export function RecipientField({
  label,
  value,
  onChange,
  autoFocus,
  onEmptyBackspaceExit,
}: {
  label: string;
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
  autoFocus?: boolean;
  /** Called when Backspace is pressed on an empty field with no pills left, so
   *  a Cc row can fold itself away instead of sitting there empty. */
  onEmptyBackspaceExit?: () => void;
}) {
  const t = useT();
  const [text, setText] = useState("");
  // The term the suggestions were fetched for travels with them. Enter may only
  // pick a suggestion that belongs to what is in the field RIGHT NOW: the fetch
  // is debounced, so a fast typist pressing Enter would otherwise get the
  // highlighted result of the string they had 200ms ago.
  const [hits, setHits] = useState<{ term: string; rows: Suggestion[] }>({ term: "", rows: [] });
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const rows = hits.rows;
  /** Only when the list describes the current text. */
  const current = hits.term === text.trim() ? rows[cursor] : undefined;

  const taken = useMemo(() => new Set(value.map((r) => r.address.toLowerCase())), [value]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Two characters before asking: one letter matches most of the book, and the
  // list that comes back is noise the operator has to read past.
  useEffect(() => {
    const term = text.trim();
    if (term.length < 2) {
      setHits({ term: "", rows: [] });
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      getClients({ q: term, pageSize: 8 })
        .then((r) => {
          if (!alive) return;
          setHits({
            term,
            rows: r.rows
              .filter((c) => c.email && !taken.has(c.email.toLowerCase()))
              .map((c) => ({ id: c.id, name: c.name, address: c.email! })),
          });
          setCursor(0);
        })
        .catch(() => alive && setHits({ term, rows: [] }));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [text, taken]);

  function add(recipient: Recipient) {
    const address = recipient.address.trim();
    if (!address || taken.has(address.toLowerCase())) {
      setText("");
      return;
    }
    onChange([...value, { ...recipient, address }]);
    setText("");
    setHits({ term: "", rows: [] });
  }

  /** Everything typed so far, split on the separators people actually paste:
   *  commas, semicolons and whitespace. Pasting a mail client's recipient line
   *  should give pills, not one long invalid address. */
  function commitTyped() {
    const parts = text.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    const seen = new Set(taken);
    for (const address of parts) {
      if (seen.has(address.toLowerCase())) continue;
      seen.add(address.toLowerCase());
      next.push({ address });
    }
    onChange(next);
    setText("");
    setHits({ term: "", rows: [] });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (rows.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setCursor((c) => (c + (e.key === "ArrowDown" ? 1 : rows.length - 1)) % rows.length);
      return;
    }
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      // Tab only commits when there is something to commit, or it would stop
      // being a way out of the field.
      if (e.key === "Tab" && !text.trim()) return;
      e.preventDefault();
      // A typed address wins over the highlighted suggestion: somebody who
      // wrote the whole thing out meant that one.
      if (current && text.trim() && !isAddress(text)) add({ address: current.address, name: current.name });
      else commitTyped();
      return;
    }
    if (e.key === "Backspace" && !text) {
      e.preventDefault();
      if (value.length) onChange(value.slice(0, -1));
      else onEmptyBackspaceExit?.();
    }
  }

  return (
    <div className="crm-rcpt-wrap">
      <label className="crm-rcpt" onClick={() => inputRef.current?.focus()}>
        <span className="crm-rcpt-lab">{label}</span>
        {value.map((r) => (
          <span key={r.address} className={cn("crm-rcpt-pill", !isAddress(r.address) && "is-bad")}>
            <Avatar name={r.name ?? r.address} size={20} />
            <b title={r.address}>{r.name ?? r.address}</b>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((x) => x.address !== r.address));
              }}
              aria-label={t("compose.removeRecipient", { address: r.address })}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          // A half-typed address must not vanish because the window lost focus.
          onBlur={() => commitTyped()}
          placeholder={value.length ? "" : t("compose.recipientPlaceholder")}
          autoComplete="off"
          spellCheck={false}
        />
      </label>

      {rows.length ? (
        <div className="crm-csug">
          {rows.map((hit, i) => (
            <button
              key={hit.id}
              type="button"
              className={cn("crm-mi", i === cursor && "is-on")}
              // mousedown, not click: the input's blur commits what is typed,
              // and by the time a click landed the suggestion list would be gone.
              onMouseDown={(e) => {
                e.preventDefault();
                add({ address: hit.address, name: hit.name });
              }}
            >
              <Avatar name={hit.name} size={26} />
              <span className="min-w-0 flex-1">
                <span className="crm-trunc block">{hit.name}</span>
                <span className="crm-mi-sub crm-trunc block">{hit.address}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
